import { useCallback } from 'react';
import { useChatStore } from '../stores';
import { chatService } from '../services';
import { classifyContent, type ContentProfile } from '../utils/contentClassifier';
import { autoCompleteStyles, applyDesignAdvice } from '../utils/styleEngine';
import { selectTemplateByKeywords } from '../utils/pageTemplates';

// AI联动事件名称
export const AI_LINKAGE_EVENT = 'ai-linkage:use-response';

/**
 * 智能处理AI回复内容
 * 根据内容类型采取不同的处理策略
 */
async function processAIContent(profile: ContentProfile): Promise<{ html: string | null; mode: string }> {
  console.log('[processAIContent] Type:', profile.type, 'Keywords:', profile.visualKeywords);
  
  switch (profile.type) {
    case 'full-page':
      // T1: 完整页面，直接渲染
      return { html: profile.htmlBlock, mode: 'identity' };
      
    case 'partial-html':
      // T2: 部分HTML，自动补全样式
      const completedHtml = autoCompleteStyles(profile.htmlBlock || '', profile.visualKeywords);
      return { html: completedHtml, mode: 'enhanced' };
      
    case 'mixed':
      // T4: 混合内容，提取主要代码块
      if (profile.htmlBlock) {
        // 如果是完整页面直接渲染，否则补全样式
        if (profile.htmlBlock.includes('<!DOCTYPE') || profile.htmlBlock.includes('<html')) {
          return { html: profile.htmlBlock, mode: 'identity' };
        } else {
          const enhanced = autoCompleteStyles(profile.htmlBlock, profile.visualKeywords);
          return { html: enhanced, mode: 'enhanced' };
        }
      }
      return { html: null, mode: 'failed' };
      
    case 'text-only':
      // T3: 纯文本描述，使用模板生成
      const templateHtml = selectTemplateByKeywords(profile.visualKeywords, profile.textHint);
      return { html: templateHtml, mode: 'generated' };
      
    case 'design-advice':
      // T5: 设计建议，应用到当前页面
      const { useDesignStore } = await import('../stores/designStore');
      const currentHtml = useDesignStore.getState().previewHtml;
      if (currentHtml) {
        const updatedHtml = applyDesignAdvice(currentHtml, profile.textHint);
        return { html: updatedHtml, mode: 'applied' };
      }
      // 如果没有当前页面，生成一个通用页面
      const genericHtml = selectTemplateByKeywords([], profile.textHint);
      return { html: genericHtml, mode: 'generated' };
      
    case 'unknown':
    default:
      // 未知类型，尝试提取代码或返回null
      if (profile.codeFragments.length > 0) {
        const firstCode = profile.codeFragments[0];
        const enhanced = autoCompleteStyles(firstCode, profile.visualKeywords);
        return { html: enhanced, mode: 'enhanced' };
      }
      return { html: null, mode: 'failed' };
  }
}

export function useChat() {
  const {
    sessions,
    currentSessionId,
    isLoading,
    error,
    streamingContent,
    isStreaming,
    createSession,
    setCurrentSession,
    addMessage,
    deleteSession,
    deleteMessage,
    clearMessages,
    getCurrentSession,
    setLoading,
    setError,
    setIsStreaming,
    clearStreamingContent,
    appendStreamingContent,
  } = useChatStore();

  const currentSession = getCurrentSession();

  // Convert session messages to history format for API
  const getSessionHistory = useCallback(() => {
    if (!currentSession) return [];
    return currentSession.messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
    }));
  }, [currentSession]);

  const sendMessage = useCallback(
    async (content: string) => {
      // Create session if none exists
      let sessionId = currentSessionId;
      if (!sessionId) {
        sessionId = createSession();
      }

      // Add user message
      addMessage(sessionId, { role: 'user', content });

      setLoading(true);
      setError(null);
      setIsStreaming(true);
      clearStreamingContent();

      try {
        // Get current history
        const history = getSessionHistory();

        // Call API with streaming
        const result = await chatService.streamBusinessMessage(
          content,
          history,
          (chunk) => {
            appendStreamingContent(chunk);
          }
        );

        // Add assistant message with full response
        addMessage(sessionId, { role: 'assistant', content: result.response });
        clearStreamingContent();

        // AI联动：保存AI回复到store
        const { setLastAIResponse } = useChatStore.getState();
        if (import.meta.env.DEV) {
          console.log('[useChat] Setting lastAIResponse, length:', result.response.length);
        }
        setLastAIResponse(result.response);

        // 智能处理AI回复内容
        console.log('[useChat] Raw response length:', result.response.length);
        console.log('[useChat] Response preview:', result.response.substring(0, 200));
        
        const contentProfile = classifyContent(result.response);
        console.log('[useChat] Content classified as:', contentProfile.type, 
                    'confidence:', contentProfile.confidence,
                    'htmlBlock:', contentProfile.htmlBlock ? '有' : '无',
                    'codeFragments:', contentProfile.codeFragments.length);
        
        const { html: processedHtml, mode } = await processAIContent(contentProfile);
        
        if (processedHtml) {
          try {
            const { useDesignStore } = await import('../stores/designStore');
            useDesignStore.getState().setPreviewHtml(processedHtml);
            console.log('[useChat] Successfully set previewHtml, mode:', mode, 'length:', processedHtml.length);
          } catch (err) {
            console.error('[useChat] Failed to set previewHtml:', err);
          }
        } else {
          console.log('[useChat] Could not generate preview from response');
        }

        // Store config if available (could be used by other components)
        if (result.config && import.meta.env.DEV) {
          console.log('[useChat] Received config:', result.config);
          // You can emit an event or update a separate store here
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '发送消息失败';
        setError(errorMessage);

        // Add error message
        addMessage(sessionId, {
          role: 'assistant',
          content: `抱歉，发生了错误：${errorMessage}。请稍后重试。`,
        });
        console.error('Chat error:', err);
      } finally {
        setLoading(false);
        setIsStreaming(false);
      }
    },
    [
      currentSessionId,
      createSession,
      addMessage,
      getSessionHistory,
      setLoading,
      setError,
      setIsStreaming,
      clearStreamingContent,
      appendStreamingContent,
    ]
  );

  const sendMessageNonStream = useCallback(
    async (content: string) => {
      let sessionId = currentSessionId;
      if (!sessionId) {
        sessionId = createSession();
      }

      addMessage(sessionId, { role: 'user', content });
      setLoading(true);
      setError(null);

      try {
        const history = getSessionHistory();
        const response = await chatService.sendBusinessMessage(content, history);

        if (response.success && response.data) {
          addMessage(sessionId, {
            role: 'assistant',
            content: response.data.response,
          });

          // AI联动：保存AI回复到store
          const { setLastAIResponse } = useChatStore.getState();
          setLastAIResponse(response.data.response);

          if (response.data.config) {
            console.log('[useChat] Received config:', response.data.config);
          }
        } else {
          throw new Error(response.error || '未知错误');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '发送消息失败';
        setError(errorMessage);
        addMessage(sessionId, {
          role: 'assistant',
          content: `抱歉，发生了错误：${errorMessage}。请稍后重试。`,
        });
        console.error('Chat error:', err);
      } finally {
        setLoading(false);
      }
    },
    [currentSessionId, createSession, addMessage, getSessionHistory, setLoading, setError]
  );

  const regenerateMessage = useCallback(
    async (messageId: string) => {
      if (!currentSession) return;

      const messageIndex = currentSession.messages.findIndex(
        (m) => m.id === messageId
      );
      if (messageIndex <= 0) return;

      const userMessage = currentSession.messages[messageIndex - 1];
      if (userMessage.role !== 'user') return;

      // Remove the assistant message we're regenerating
      deleteMessage(currentSession.id, messageId);

      setLoading(true);
      setIsStreaming(true);
      clearStreamingContent();

      try {
        // Get history up to the user message
        const history = currentSession.messages
          .slice(0, messageIndex - 1)
          .map((msg) => ({
            role: msg.role,
            content: msg.content,
          }));

        const result = await chatService.streamBusinessMessage(
          userMessage.content,
          history,
          (chunk) => {
            appendStreamingContent(chunk);
          }
        );

        addMessage(currentSession.id, {
          role: 'assistant',
          content: result.response,
        });
        clearStreamingContent();

        // AI联动：保存AI回复到store
        const { setLastAIResponse } = useChatStore.getState();
        setLastAIResponse(result.response);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '重新生成失败';
        setError(errorMessage);
        addMessage(currentSession.id, {
          role: 'assistant',
          content: `抱歉，重新生成失败：${errorMessage}。请稍后重试。`,
        });
      } finally {
        setLoading(false);
        setIsStreaming(false);
      }
    },
    [
      currentSession,
      deleteMessage,
      addMessage,
      setLoading,
      setError,
      setIsStreaming,
      clearStreamingContent,
      appendStreamingContent,
    ]
  );

  const clearCurrentSession = useCallback(() => {
    if (currentSessionId) {
      clearMessages(currentSessionId);
    }
  }, [currentSessionId, clearMessages]);

  return {
    // State
    sessions,
    currentSession,
    currentSessionId,
    isLoading,
    error,
    streamingContent,
    isStreaming,

    // Actions
    createSession,
    setCurrentSession,
    sendMessage,
    sendMessageNonStream,
    deleteSession,
    deleteMessage,
    clearCurrentSession,
    regenerateMessage,
  };
}
